import type { TranscriptionLanguagePage } from '@/constants/transcriptionLanguagePages';

import {
  AUDIO_EXAMPLE,
  HUB_EXAMPLE,
  INSTAGRAM_EXAMPLE,
  INTERVIEW_EXAMPLE,
  LECTURE_EXAMPLE,
  M4A_EXAMPLE,
  MEETING_EXAMPLE,
  MP3_EXAMPLE,
  MP4_EXAMPLE,
  NOTE_TAKER_EXAMPLE,
  PODCAST_EXAMPLE,
  TIKTOK_EXAMPLE,
  VIDEO_EXAMPLE,
  VOICE_MEMO_EXAMPLE,
  WAV_EXAMPLE,
  YOUTUBE_EXAMPLE,
  YOUTUBE_SUMMARY_EXAMPLE,
  ZOOM_EXAMPLE,
} from './examples';
import type { LandingCopy, LandingStep } from './pages';
import type { TranscriptExample } from './TranscriptMock';

/*
 * One config per transcription landing page. Each title starts with the
 * phrase people search for (DataForSEO, US, October 2026), e.g. "transcribe
 * audio to text", "youtube transcript generator", "tiktok transcript",
 * "mp4 to text". Seo adds " | Easy-Peasy.AI" to `seoTitle`; keep the whole
 * title under 60 characters. All text is English keys.
 */

const LANGUAGE_STEP: LandingStep = {
  title: 'Pick the language',
  body: 'Choose the language spoken in the recording, or leave it on automatic. Turn on speaker detection when more than one person talks.',
};

const RESULT_STEP: LandingStep = {
  title: 'Get your transcript',
  body: 'Read it with speaker names and timestamps, copy it, download subtitles, or generate a summary and notes from it.',
};

const VIDEO_RESULT_STEP: LandingStep = {
  title: 'Get the transcript and subtitles',
  body: 'Read the transcript with timestamps, download SRT or VTT subtitles, or generate chapters and a summary.',
};

const LINK_STEP: LandingStep = {
  title: 'Paste the link',
  body: 'Copy the link in the app or your browser and paste it on this page. The video needs to be public.',
};

const REUSE_STEP: LandingStep = {
  title: 'Reuse the words',
  body: 'Copy the text, download captions, or turn it into a summary, a script or a post.',
};

const SPEAKERS_STEP: LandingStep = {
  title: 'Speakers are told apart',
  body: 'Turn on speaker detection and name each person once. Every line shows who said it and when.',
};

const NOTES_STEP: LandingStep = {
  title: 'Get notes and action items',
  body: 'Generate a summary, action items and the questions raised, then share them with your team.',
};

const uploadStep = (format: string): LandingStep => ({
  title: `Upload your ${format}`,
  body: `Drop the ${format} file on this page or choose it from your computer or phone.`,
});

export const HUB_PAGE: LandingCopy = {
  id: 'hub',
  path: '/ai-transcription',
  seoTitle: 'AI Transcription: Audio & Video to Text',
  seoDescription:
    'AI transcription for audio and video. Upload a file or paste a link and get a transcript with speaker names, timestamps, a summary and subtitles in {{languages}} languages.',
  ogTitle: 'AI transcription for audio and video',
  eyebrow: 'AI transcription in {{languages}} languages',
  title: 'AI transcription',
  titleTail: 'for audio and video.',
  lead: 'Upload a recording or paste a link and get an accurate transcript with speaker names and timestamps, plus a summary, notes and subtitles.',
  example: HUB_EXAMPLE,
  steps: [
    {
      title: 'Add your recording',
      body: 'Upload an audio or video file, record in your browser, or paste a YouTube, Instagram, TikTok or Google Drive link.',
    },
    LANGUAGE_STEP,
    RESULT_STEP,
  ],
  details: {
    title: 'One place for every recording.',
    tail: 'Searchable, shareable, yours.',
    items: [
      {
        title: 'A library of everything you’ve recorded',
        body: 'Every transcript is saved to your account, so you can search, reopen and share past recordings at any time.',
      },
      {
        title: 'From transcript to finished notes',
        body: 'Turn a recording into a summary, meeting minutes, show notes, an article or social posts without copying text between apps.',
      },
      {
        title: 'Works on any device',
        body: 'Upload from your computer, record on your phone, or paste a link from anywhere. Nothing to install.',
      },
    ],
  },
  faqs: [
    {
      question: 'What is AI transcription?',
      answer:
        'AI transcription turns speech in an audio or video recording into written text automatically. Easy-Peasy.AI also labels each speaker, adds timestamps and can write a summary and notes from the transcript.',
    },
    {
      question: 'How do I transcribe a recording?',
      answer:
        'Upload your file or paste a link on this page, pick the language and start the transcription. Most recordings are ready in a few minutes.',
    },
    {
      question: 'Can I transcribe video files too?',
      answer:
        'Yes. Upload an MP4, MOV, WEBM or another video file, or paste a YouTube, Instagram, TikTok or Google Drive link, and the speech in the video is transcribed.',
    },
    {
      question: 'Can my team use it?',
      answer:
        'Yes. On a Teams plan, each person gets their own transcriptions, and you can share any transcript with your team in one click.',
    },
  ],
};

export const AUDIO_TO_TEXT_PAGE: LandingCopy = {
  id: 'audio-to-text',
  path: '/ai-transcription/audio-to-text-converter',
  seoTitle: 'Transcribe Audio to Text Free Online',
  seoDescription:
    'Transcribe audio to text online. Upload MP3, WAV, M4A or any audio file and get an accurate transcript with speaker labels and timestamps in minutes. Free to try.',
  ogTitle: 'Transcribe audio to text',
  eyebrow: 'Audio to text converter',
  title: 'Transcribe audio to text',
  titleTail: 'online, in minutes.',
  lead: 'Turn interviews, calls, voice notes and recordings into text you can search, copy and share, with every speaker labelled.',
  example: AUDIO_EXAMPLE,
  steps: [uploadStep('audio'), LANGUAGE_STEP, RESULT_STEP],
  details: {
    title: 'Audio in, clean text out.',
    tail: 'Whatever you recorded it on.',
    items: [
      {
        title: 'Any audio format',
        body: 'MP3, WAV, M4A, AAC, FLAC, OGG, OPUS and more, straight from your phone, recorder or computer. No converting first.',
      },
      {
        title: 'Readable from the first line',
        body: 'Punctuation, paragraphs and speaker labels are added for you, so the transcript reads like a document, not a wall of words.',
      },
      {
        title: 'Find any moment',
        body: 'Every line has a timestamp. Click it to hear that part of the recording again.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I convert audio to text?',
      answer:
        'Upload the audio file on this page, choose the language and start the transcription. The text is ready in a few minutes, with speaker names and timestamps.',
    },
    {
      question: 'Which audio formats can I convert to text?',
      answer:
        'MP3, WAV, M4A, AAC, FLAC, OGG, OPUS, WMA, AMR, AIFF and about 30 more. If your phone or recorder saved it, it will almost certainly work.',
    },
    {
      question: 'How long does it take to transcribe audio?',
      answer:
        'Usually a few minutes, depending on the length of the recording. You can close the page while it works; the transcript waits in your account.',
    },
  ],
};

export const VIDEO_TO_TEXT_PAGE: LandingCopy = {
  id: 'video-to-text',
  path: '/ai-transcription/video-to-text-converter',
  seoTitle: 'Transcribe Video to Text Free Online',
  seoDescription:
    'Transcribe video to text with AI. Upload MP4, MOV or WEBM, or paste a YouTube, Instagram or TikTok link. Get a transcript, subtitles and chapters in minutes.',
  ogTitle: 'Transcribe video to text',
  eyebrow: 'Video transcription',
  title: 'Transcribe video to text',
  titleTail: 'and get subtitles too.',
  lead: 'Get the full transcript of any video, then download subtitles, pull out chapters or turn it into a blog post.',
  example: VIDEO_EXAMPLE,
  steps: [
    {
      title: 'Upload a video or paste a link',
      body: 'Drop in an MP4, MOV or WEBM file, or paste a YouTube, Instagram, TikTok or Google Drive link.',
    },
    LANGUAGE_STEP,
    VIDEO_RESULT_STEP,
  ],
  details: {
    title: 'Made for video.',
    tail: 'Not just audio with pictures.',
    items: [
      {
        title: 'Files or links',
        body: 'Upload a video from your computer or phone, or paste a link to a YouTube, Instagram, TikTok or Google Drive video.',
      },
      {
        title: 'Subtitles that line up',
        body: 'Download SRT or VTT files timed to the speech, ready for YouTube, Premiere Pro, DaVinci Resolve or CapCut.',
      },
      {
        title: 'Chapters and summaries',
        body: 'Get a timestamped overview for YouTube chapters, a summary, or an article written from the video.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I transcribe a video to text?',
      answer:
        'Upload the video or paste its link on this page, pick the language and start. The transcript is ready in a few minutes, with timestamps.',
    },
    {
      question: 'Can I get subtitles for my video?',
      answer:
        'Yes. Every transcript can be downloaded as an SRT or VTT subtitle file, ready to upload to YouTube or add in your video editor.',
    },
    {
      question: 'Which video formats are supported?',
      answer:
        'MP4, MOV, WEBM, M4V, MKV, MTS and other common formats. You can also paste a link to a public YouTube, Instagram or TikTok video instead of uploading.',
    },
  ],
};

export const MP3_TO_TEXT_PAGE: LandingCopy = {
  id: 'mp3-to-text',
  path: '/ai-transcription/mp3-to-text-converter',
  seoTitle: 'MP3 to Text Converter: Transcribe MP3 Free',
  seoDescription:
    'Convert MP3 to text online. Upload an MP3 file and get an accurate transcript with speaker labels, timestamps and a summary in minutes. Free to try.',
  ogTitle: 'Convert MP3 to text',
  eyebrow: 'MP3 to text converter',
  title: 'Convert MP3 to text',
  titleTail: 'in a few clicks.',
  lead: 'Upload an MP3 of an interview, a podcast or a recording and get a clean transcript with speaker names and timestamps.',
  example: MP3_EXAMPLE,
  steps: [uploadStep('MP3'), LANGUAGE_STEP, RESULT_STEP],
  details: {
    title: 'MP3 to text,',
    tail: 'without the busywork.',
    items: [
      {
        title: 'Any MP3, any length',
        body: 'Podcasts, interviews, lectures and voice recordings. Each file counts as one transcription, whatever its length.',
      },
      {
        title: 'Names instead of “Speaker A”',
        body: 'Speakers are told apart for you. Name them once and every line shows who is talking.',
      },
      {
        title: 'More than a transcript',
        body: 'Generate a summary, key points or an article from the MP3, or ask questions about it in a chat.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I convert an MP3 to text?',
      answer:
        'Upload the MP3 on this page, choose the spoken language and start the transcription. The text is ready in a few minutes.',
    },
    {
      question: 'Is the MP3 to text converter free?',
      answer:
        'Yes, you can convert MP3 files to text with the free transcriptions included in the Free plan every month. Paid plans add more.',
    },
    {
      question: 'Can I export the transcript of my MP3?',
      answer:
        'Yes. Copy the text in one click, download it as SRT or VTT subtitles, or share a link to the transcript.',
    },
  ],
};

export const MP4_TO_TEXT_PAGE: LandingCopy = {
  id: 'mp4-to-text',
  path: '/ai-transcription/mp4-to-text-converter',
  seoTitle: 'MP4 to Text Converter: Transcribe MP4 Free',
  seoDescription:
    'Convert MP4 to text and get a transcript with speaker labels and timestamps, plus SRT subtitles and chapters, in minutes. Upload your video. Free to try.',
  ogTitle: 'Convert MP4 to text',
  eyebrow: 'MP4 to text converter',
  title: 'Convert MP4 to text',
  titleTail: 'with subtitles included.',
  lead: 'Upload an MP4 of a webinar, a meeting or a video and get the full transcript, subtitles and chapters.',
  example: MP4_EXAMPLE,
  steps: [uploadStep('MP4'), LANGUAGE_STEP, VIDEO_RESULT_STEP],
  details: {
    title: 'Straight from the MP4.',
    tail: 'No audio extraction needed.',
    items: [
      {
        title: 'Upload the video as it is',
        body: 'There’s no need to pull out the audio first. The speech is transcribed straight from the MP4.',
      },
      {
        title: 'A transcript and subtitles',
        body: 'Get the transcript and SRT or VTT subtitles from the same upload, timed to the speech.',
      },
      {
        title: 'Webinars, meetings and screen recordings',
        body: 'Long recordings are fine. Each file counts as one transcription, however long it runs.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I get a transcript from an MP4?',
      answer:
        'Upload the MP4 on this page, pick the language and start. The transcript is ready in a few minutes, and you can download subtitles from it too.',
    },
    {
      question: 'Do I need to convert my MP4 to audio first?',
      answer:
        'No. Upload the MP4 as it is and the speech is transcribed straight from the video.',
    },
    {
      question: 'Can I get subtitles from an MP4?',
      answer:
        'Yes. Download the transcript as an SRT or VTT file and add it to the video in YouTube or your video editor.',
    },
  ],
};

export const M4A_TO_TEXT_PAGE: LandingCopy = {
  id: 'm4a-to-text',
  path: '/ai-transcription/m4a-to-text-converter',
  seoTitle: 'M4A to Text Converter: Transcribe M4A Free',
  seoDescription:
    'Convert M4A to text online. Upload an M4A from your iPhone, Mac or recorder and get a transcript with speaker labels and timestamps in minutes. Free to try.',
  ogTitle: 'Convert M4A to text',
  eyebrow: 'M4A to text converter',
  title: 'Convert M4A to text',
  titleTail: 'straight from your iPhone.',
  lead: 'iPhones, Macs and many recorders save audio as M4A. Upload the file and get the transcript, a summary and next steps.',
  example: M4A_EXAMPLE,
  steps: [uploadStep('M4A'), LANGUAGE_STEP, RESULT_STEP],
  details: {
    title: 'Made for the files',
    tail: 'your phone already saves.',
    items: [
      {
        title: 'Upload from your phone',
        body: 'Open this page on your iPhone or Android phone and choose the recording. No app to install.',
      },
      {
        title: 'Calls, memos and recordings',
        body: 'Recordings from Voice Memos, call recorders and Mac apps such as GarageBand and QuickTime are usually M4A files.',
      },
      {
        title: 'Next steps, written for you',
        body: 'Generate a summary and action items from the recording, then copy them anywhere.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I transcribe an M4A file from my iPhone?',
      answer:
        'Open this page on your iPhone, tap to choose a file and pick the recording from Files. If it’s in Voice Memos, share it to Files first.',
    },
    {
      question: 'Do I need to convert M4A to MP3 first?',
      answer: 'No. Upload the M4A as it is.',
    },
    {
      question: 'Can I transcribe M4A files on a Mac?',
      answer:
        'Yes. Drag the M4A onto this page in any browser and the transcript is ready in a few minutes.',
    },
  ],
};

export const WAV_TO_TEXT_PAGE: LandingCopy = {
  id: 'wav-to-text',
  path: '/ai-transcription/wav-to-text-converter',
  seoTitle: 'WAV to Text Converter: Transcribe WAV Free',
  seoDescription:
    'Convert WAV to text online. Upload a WAV recording and get an accurate transcript with speaker labels and timestamps in minutes. Free to try.',
  ogTitle: 'Convert WAV to text',
  eyebrow: 'WAV to text converter',
  title: 'Convert WAV to text',
  titleTail: 'with every speaker labelled.',
  lead: 'Recorders and studios save in WAV. Upload the file as it is and get a transcript with names and timestamps.',
  example: WAV_EXAMPLE,
  steps: [uploadStep('WAV'), LANGUAGE_STEP, RESULT_STEP],
  details: {
    title: 'Studio quality in,',
    tail: 'clean text out.',
    items: [
      {
        title: 'No compressing first',
        body: 'Upload the WAV exactly as your recorder or audio interface saved it. Large files upload in the background.',
      },
      {
        title: 'Clear audio, accurate text',
        body: 'WAV recordings keep every detail of the voice, which is exactly what speech recognition likes.',
      },
      {
        title: 'Ready for editing',
        body: 'Use the timestamps to find the takes you want, or download subtitles for the finished video.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I convert a WAV file to text?',
      answer:
        'Upload the WAV on this page, choose the language and start the transcription. The text is ready in a few minutes.',
    },
    {
      question: 'Do I need to compress my WAV file first?',
      answer:
        'No. Upload the WAV as it is; there’s no need to convert it to MP3 first.',
    },
    {
      question: 'Does it work with recordings from a voice recorder?',
      answer:
        'Yes. Handheld recorders, microphones and audio interfaces usually save WAV files, and they work well, especially with speaker detection turned on for interviews.',
    },
  ],
};

export const MEETING_PAGE: LandingCopy = {
  id: 'meeting',
  path: '/ai-transcription/meeting',
  seoTitle: 'Meeting Transcription & AI Meeting Notes',
  seoDescription:
    'Meeting transcription with AI notes: a summary, decisions and action items, with who said what. Upload a recording or record the meeting in your browser.',
  ogTitle: 'Meeting transcription and AI notes',
  eyebrow: 'Meeting transcription',
  title: 'Meeting transcription',
  titleTail: 'and AI meeting notes.',
  lead: 'Upload a meeting recording or record one in your browser. Get the transcript with who said what, a summary and the next steps.',
  example: MEETING_EXAMPLE,
  steps: [
    {
      title: 'Record or upload the meeting',
      body: 'Record in your browser with the AI Note Taker, or upload a recording from Zoom, Google Meet or Microsoft Teams.',
    },
    SPEAKERS_STEP,
    NOTES_STEP,
  ],
  details: {
    title: 'Notes nobody has to take.',
    tail: 'Decisions nobody forgets.',
    items: [
      {
        title: 'In the room or online',
        body: 'Record an in-person meeting on your laptop or phone, or upload the recording of a video call.',
      },
      {
        title: 'Action items with owners',
        body: 'Get the decisions made, the open questions and who does what next, written from the conversation.',
      },
      {
        title: 'Share with everyone who missed it',
        body: 'Send a link to the transcript and notes, or share it with your team in one click.',
      },
    ],
  },
  faqs: [
    {
      question: 'Does it work with Zoom, Google Meet and Teams recordings?',
      answer:
        'Yes. Download the recording from Zoom, Google Meet or Microsoft Teams and upload it, or paste a Google Drive link to it.',
    },
    {
      question: 'Can I record an in-person meeting?',
      answer:
        'Yes. Open the AI Note Taker in your browser, put your phone or laptop on the table and start recording. You get the transcript and notes when you stop.',
    },
    {
      question: 'Does a bot join my calls?',
      answer:
        'No. Nothing joins your calls. You upload a recording, or record the meeting yourself in your browser.',
    },
  ],
};

export const ZOOM_PAGE: LandingCopy = {
  id: 'zoom',
  path: '/ai-transcription/zoom',
  seoTitle: 'Transcribe Zoom Recordings to Text Free',
  seoDescription:
    'Zoom transcription for your recordings, plus Google Meet and Microsoft Teams. Upload the recording and get a transcript with speaker names, a summary and action items.',
  ogTitle: 'Transcribe Zoom recordings to text',
  eyebrow: 'Zoom transcription',
  title: 'Transcribe Zoom recordings',
  titleTail: 'into notes and next steps.',
  lead: 'Upload a recording from Zoom, Google Meet or Microsoft Teams and get the transcript with who said what, a summary and action items.',
  example: ZOOM_EXAMPLE,
  steps: [
    {
      title: 'Upload the recording',
      body: 'Upload the MP4 or M4A that Zoom saved, or a recording from Google Meet or Microsoft Teams.',
    },
    SPEAKERS_STEP,
    NOTES_STEP,
  ],
  details: {
    title: 'Your call recordings,',
    tail: 'finally useful.',
    items: [
      {
        title: 'Works with every platform',
        body: 'Zoom, Google Meet, Microsoft Teams, Webex: if it saves a recording, you can transcribe it.',
      },
      {
        title: 'Audio-only files are fine',
        body: 'Zoom also saves an M4A of the audio. It’s smaller, uploads faster and gives the same transcript.',
      },
      {
        title: 'No bot in your meeting',
        body: 'Nothing joins your calls. You choose which recordings to transcribe.',
      },
    ],
  },
  faqs: [
    {
      question: 'Where do I find my Zoom recording?',
      answer:
        'Local recordings are saved in the Zoom folder in your Documents. Cloud recordings can be downloaded from the Recordings page of your Zoom account.',
    },
    {
      question: 'Can I transcribe Google Meet and Teams recordings too?',
      answer:
        'Yes. Download the recording and upload it, or paste a Google Drive link to a Meet recording that’s shared with anyone who has the link.',
    },
    {
      question: 'Will my colleagues’ names show in the transcript?',
      answer:
        'Speakers are labelled automatically. Give each one a name once, and the name shows on every line they speak.',
    },
  ],
};

export const INTERVIEW_PAGE: LandingCopy = {
  id: 'interview',
  path: '/ai-transcription/interview',
  seoTitle: 'Interview Transcription: Free AI Transcripts',
  seoDescription:
    'Interview transcription with speaker labels and a timestamp for every quote. For journalists, researchers and recruiters. Upload a recording or paste a link.',
  ogTitle: 'Interview transcription with AI',
  eyebrow: 'Interview transcription',
  title: 'Interview transcription',
  titleTail: 'with every quote timed.',
  lead: 'Upload an interview and get a transcript with each speaker labelled and a timestamp on every line, so quotes are easy to find and check.',
  example: INTERVIEW_EXAMPLE,
  steps: [
    {
      title: 'Upload the interview',
      body: 'Upload the recording from your phone, recorder or video call, or paste a link to it.',
    },
    {
      title: 'Label the speakers',
      body: 'Turn on speaker detection, then name the interviewer and the guest once. Every line shows who said it and when.',
    },
    {
      title: 'Find and use quotes',
      body: 'Jump to any line by its timestamp, ask the transcript a question, or generate a summary and key points.',
    },
  ],
  details: {
    title: 'Built for people who quote.',
    tail: 'Journalists, researchers, recruiters.',
    items: [
      {
        title: 'Check every quote',
        body: 'Each line has a timestamp, so you can play back the original before you publish a quote.',
      },
      {
        title: 'Hours of research, searchable',
        body: 'Keep every interview in one library, and ask a transcript where a topic came up.',
      },
      {
        title: 'Confidential by default',
        body: 'Transcripts stay private unless you share them, under SOC 2, ISO 27001, GDPR and HIPAA standards.',
      },
    ],
  },
  faqs: [
    {
      question: 'Is it accurate enough for quotes?',
      answer:
        'For clear recordings, the transcript is close to word for word. Every line has a timestamp, so you can play back the original and check a quote before you use it.',
    },
    {
      question: 'Can it transcribe interviews in other languages?',
      answer:
        'Yes, in {{languages}} languages. Choose the language spoken in the interview for the best accuracy.',
    },
    {
      question: 'Are my interviews kept confidential?',
      answer:
        'Yes. Transcripts are private to you unless you share them, and we don’t use your recordings to train our own models.',
    },
  ],
};

export const PODCAST_PAGE: LandingCopy = {
  id: 'podcast',
  path: '/ai-transcription/podcast',
  seoTitle: 'Podcast Transcription & Show Notes with AI',
  seoDescription:
    'Podcast transcription with AI. Get a full transcript with speaker names, show notes, chapters with timestamps and a blog post from every episode.',
  ogTitle: 'Podcast transcription and show notes',
  eyebrow: 'Podcast transcription',
  title: 'Podcast transcription',
  titleTail: 'with show notes and chapters.',
  lead: 'Upload an episode and get the transcript with every speaker named, then show notes, chapters and an article to publish.',
  example: PODCAST_EXAMPLE,
  steps: [
    {
      title: 'Upload your episode',
      body: 'Drop in the MP3 or WAV of your episode, or paste a link to the video version.',
    },
    {
      title: 'Hosts and guests are labelled',
      body: 'Speaker detection tells hosts and guests apart. Give each one a name and every line shows it.',
    },
    {
      title: 'Publish show notes and more',
      body: 'Generate show notes, chapters with timestamps, an article and social posts from the episode.',
    },
  ],
  details: {
    title: 'One episode,',
    tail: 'everything you publish.',
    items: [
      {
        title: 'Show notes in minutes',
        body: 'A summary of the episode, the topics covered and timestamps for each chapter, ready to paste into your podcast host.',
      },
      {
        title: 'An article for your website',
        body: 'Turn the episode into a blog post so it can be found on Google, not just in podcast apps.',
      },
      {
        title: 'Posts, threads and scripts',
        body: 'Get posts for LinkedIn and X, a newsletter and a script for a short video from the episode.',
      },
    ],
  },
  faqs: [
    {
      question: 'Can it write my podcast show notes?',
      answer:
        'Yes. From the transcript, generate show notes, a timestamped overview for chapters, an article for your website and posts for LinkedIn or X.',
    },
    {
      question: 'Why publish a podcast transcript?',
      answer:
        'A transcript makes your episode searchable, accessible to people who can’t listen, and easy to quote and share.',
    },
    {
      question: 'Does it work with video podcasts?',
      answer:
        'Yes. Upload the video file or paste a YouTube link, and download SRT subtitles for the video as well.',
    },
  ],
};

export const LECTURE_PAGE: LandingCopy = {
  id: 'lecture',
  path: '/ai-transcription/lecture',
  seoTitle: 'AI Lecture Recorder: Transcribe & Take Notes',
  seoDescription:
    'Record lectures in your browser or upload a recording, and get the transcript, key points and study notes. An AI lecture recorder and note taker for students.',
  ogTitle: 'AI lecture recorder and note taker',
  eyebrow: 'AI lecture recorder',
  title: 'AI lecture recorder',
  titleTail: 'and note taker.',
  lead: 'Record a class in your browser or upload the recording. Get the full transcript, the key points and notes to revise from.',
  example: LECTURE_EXAMPLE,
  steps: [
    {
      title: 'Record or upload the lecture',
      body: 'Record in your browser with the AI Note Taker, for up to 2 hours, or upload a recording you already have.',
    },
    LANGUAGE_STEP,
    {
      title: 'Revise from your notes',
      body: 'Read the transcript with timestamps, then generate a summary, topics and key terms to study.',
    },
  ],
  details: {
    title: 'Listen in class.',
    tail: 'Let the notes write themselves.',
    items: [
      {
        title: 'Record on your phone or laptop',
        body: 'Start recording in your browser when the lecture begins. Up to 2 hours per recording.',
      },
      {
        title: 'Study notes, not just a transcript',
        body: 'Get a summary, the main topics and key terms from every class, ready for revision.',
      },
      {
        title: 'Ask about anything you missed',
        body: 'Chat with the lecture to explain a part again, find where a topic came up, or make practice questions.',
      },
    ],
  },
  faqs: [
    {
      question: 'Can I record a lecture on my phone?',
      answer:
        'Yes. Open the AI Note Taker in your phone’s browser and start recording. When you stop, the lecture is transcribed and turned into notes.',
    },
    {
      question: 'Can I ask questions about the lecture?',
      answer:
        'Yes. Chat with the transcript to explain a part again, find where a topic came up, or make practice questions.',
    },
    {
      question: 'Do I need permission to record a lecture?',
      answer:
        'Many schools allow recording for personal study, but rules differ. Check with your teacher or school before you record.',
    },
  ],
};

export const YOUTUBE_PAGE: LandingCopy = {
  id: 'youtube',
  link: {
    label: 'Paste a YouTube link',
    placeholder: 'https://www.youtube.com/watch?v=…',
    source: 'youtube',
  },
  path: '/ai-transcription/youtube',
  seoTitle: 'YouTube Transcript Generator: Free & Instant',
  seoDescription:
    'Get the transcript of any YouTube video. Paste the link and get the full text with timestamps, chapters, subtitles and a summary in seconds. Free to try.',
  ogTitle: 'YouTube transcript generator',
  eyebrow: 'YouTube transcript generator',
  title: 'YouTube transcript generator',
  titleTail: 'for any video.',
  lead: 'Paste a YouTube link and get the full transcript with timestamps, then chapters, subtitles, a summary or a blog post.',
  example: YOUTUBE_EXAMPLE,
  steps: [
    {
      title: 'Paste the YouTube link',
      body: 'Copy the video’s link from YouTube and paste it on this page.',
    },
    {
      title: 'Get the transcript',
      body: 'Videos with captions are ready in seconds. Videos without captions are transcribed from their audio.',
    },
    {
      title: 'Use it anywhere',
      body: 'Copy the text, download subtitles, or turn the video into chapters, a summary or an article.',
    },
  ],
  details: {
    title: 'Any YouTube video,',
    tail: 'as text you can use.',
    items: [
      {
        title: 'Works without captions',
        body: 'No captions on the video? Its audio is transcribed instead, so you always get a transcript.',
      },
      {
        title: 'In the original language',
        body: 'The transcript comes in the language the video is spoken in, even when YouTube has dubbed it into others.',
      },
      {
        title: 'Turn videos into content',
        body: 'Get chapters, a summary, an article or a thread from any video, with the timestamps kept.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I get the transcript of a YouTube video?',
      answer:
        'Copy the video’s link, paste it on this page and start the transcription. The transcript appears with timestamps, ready to copy or download.',
    },
    {
      question: 'Does it work for videos without captions?',
      answer:
        'Yes. When a video has no captions, its audio is transcribed instead. That takes a little longer than a video with captions.',
    },
    {
      question: 'How long can the video be?',
      answer:
        'Videos up to 6 hours long. Live streams can be transcribed once they have ended.',
    },
  ],
};

export const YOUTUBE_SUMMARIZER_PAGE: LandingCopy = {
  id: 'youtube-summarizer',
  link: {
    label: 'Paste a YouTube link',
    placeholder: 'https://www.youtube.com/watch?v=…',
    source: 'youtube',
  },
  path: '/ai-transcription/youtube-summarizer',
  seoTitle: 'Free YouTube Video Summarizer',
  seoDescription:
    'Summarize any YouTube video with AI. Paste the link and get a summary, key points and chapters, with the full transcript to check every detail. Free to try.',
  ogTitle: 'YouTube video summarizer',
  eyebrow: 'YouTube video summarizer',
  title: 'YouTube video summarizer',
  titleTail: 'with the full transcript.',
  lead: 'Paste a YouTube link and get a short summary and the key points, with the full transcript and timestamps when you want the details.',
  example: YOUTUBE_SUMMARY_EXAMPLE,
  steps: [
    {
      title: 'Paste the YouTube link',
      body: 'Copy the video’s link from YouTube and paste it on this page.',
    },
    {
      title: 'Get the transcript',
      body: 'Videos with captions are ready in seconds. Videos without captions are transcribed from their audio.',
    },
    {
      title: 'Summarize it',
      body: 'Generate a summary, key points or chapters, or ask the video a question in a chat.',
    },
  ],
  details: {
    title: 'Watch less.',
    tail: 'Know what was said.',
    items: [
      {
        title: 'The point in a minute',
        body: 'A summary and key points of any video, from a ten-minute tutorial to a three-hour podcast.',
      },
      {
        title: 'Check any claim',
        body: 'The full transcript sits next to the summary, with timestamps, so you can see exactly what was said.',
      },
      {
        title: 'Ask follow-up questions',
        body: 'Chat with the video to dig into a part, compare ideas or get a list of the steps it explains.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I summarize a YouTube video?',
      answer:
        'Paste the video’s link on this page and start the transcription. When it’s ready, generate a summary or key points with one click.',
    },
    {
      question: 'Can I summarize long videos?',
      answer:
        'Yes. Videos up to 6 hours long can be transcribed and summarized, such as lectures, podcasts and webinars.',
    },
    {
      question: 'Can I ask questions about the video?',
      answer:
        'Yes. Chat with the transcript to ask about any part of the video, or to get the steps it explains as a list.',
    },
  ],
};

export const TIKTOK_PAGE: LandingCopy = {
  id: 'tiktok',
  link: {
    label: 'Paste a TikTok link',
    placeholder: 'https://www.tiktok.com/@creator/video/…',
    source: 'tiktok',
  },
  path: '/ai-transcription/tiktok-transcript-generator',
  seoTitle: 'Free TikTok Transcript Generator',
  seoDescription:
    'Get the transcript of any TikTok video. Paste the link and get every word with timestamps, a summary and captions in minutes. Free to try.',
  ogTitle: 'TikTok transcript generator',
  eyebrow: 'TikTok transcript generator',
  title: 'TikTok transcript generator',
  titleTail: 'for any video.',
  lead: 'Paste a TikTok link and get every word as text, with timestamps. Quote it, caption it or turn it into a post.',
  example: TIKTOK_EXAMPLE,
  steps: [
    LINK_STEP,
    {
      title: 'Get the transcript',
      body: 'The speech in the video is transcribed with timestamps, in any of {{languages}} languages.',
    },
    REUSE_STEP,
  ],
  details: {
    title: 'Every word of a TikTok,',
    tail: 'ready to reuse.',
    items: [
      {
        title: 'Save scripts that work',
        body: 'Keep the words of videos that went viral in your niche and study how they hook viewers.',
      },
      {
        title: 'Captions for your own videos',
        body: 'Download SRT or VTT captions for your TikToks, or reuse the text in your description.',
      },
      {
        title: 'Turn short videos into posts',
        body: 'Rewrite a TikTok as a LinkedIn post, a thread or a newsletter in one click.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I get the transcript of a TikTok video?',
      answer:
        'In TikTok, tap Share and then Copy link, and paste the link on this page. The transcript is ready in a minute or two.',
    },
    {
      question: 'Can I transcribe my own TikToks for captions?',
      answer:
        'Yes. Paste the link or upload the video file, then download the transcript as an SRT or VTT caption file.',
    },
    {
      question: 'Does it work for TikToks in other languages?',
      answer:
        'Yes. Speech in {{languages}} languages can be transcribed, and the language can be detected for you.',
    },
  ],
};

export const INSTAGRAM_PAGE: LandingCopy = {
  id: 'instagram',
  link: {
    label: 'Paste an Instagram reel or video link',
    placeholder: 'https://www.instagram.com/reel/…',
    source: 'instagram',
  },
  path: '/ai-transcription/instagram-transcript-generator',
  seoTitle: 'Free Instagram Reel Transcript Generator',
  seoDescription:
    'Get the transcript of any Instagram reel or video. Paste the link and get every word with timestamps and a summary in minutes. Free to try.',
  ogTitle: 'Instagram reel transcript generator',
  eyebrow: 'Instagram transcript generator',
  title: 'Instagram transcript generator',
  titleTail: 'for reels and videos.',
  lead: 'Paste a link to a reel or video and get every word as text, ready to quote, caption or turn into a post.',
  example: INSTAGRAM_EXAMPLE,
  steps: [
    LINK_STEP,
    {
      title: 'Get the transcript',
      body: 'The speech in the reel is transcribed with timestamps, in any of {{languages}} languages.',
    },
    REUSE_STEP,
  ],
  details: {
    title: 'Reels you can read,',
    tail: 'search and reuse.',
    items: [
      {
        title: 'Keep recipes, tips and tutorials',
        body: 'Save the steps from a reel as text instead of rewatching it ten times.',
      },
      {
        title: 'Captions for your own reels',
        body: 'Download SRT or VTT captions, or reuse the words in your caption and alt text.',
      },
      {
        title: 'Repurpose what works',
        body: 'Turn a reel into a carousel script, a blog post or a newsletter in one click.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I transcribe an Instagram reel?',
      answer:
        'Open the reel, tap the share icon and Copy link, then paste the link on this page. The reel needs to be from a public account.',
    },
    {
      question: 'Can I get captions for my own reels?',
      answer:
        'Yes. Paste the link or upload the video, then download the transcript as an SRT or VTT caption file.',
    },
    {
      question: 'Can I transcribe Instagram videos, not just reels?',
      answer:
        'Yes. Any public Instagram video with speech can be transcribed from its link.',
    },
  ],
};

export const VOICE_MEMO_PAGE: LandingCopy = {
  id: 'voice-memo',
  path: '/ai-transcription/voice-memo-to-text',
  seoTitle: 'Voice Memo to Text: Transcribe iPhone Memos',
  seoDescription:
    'Transcribe voice memos to text. Upload a recording from iPhone Voice Memos or your Android recorder and get the text, a summary and a to-do list in minutes.',
  ogTitle: 'Transcribe voice memos to text',
  eyebrow: 'Voice memo to text',
  title: 'Transcribe voice memos',
  titleTail: 'into text and to-do lists.',
  lead: 'Talk through an idea, a plan or a list, then upload the memo. Get the text, a short summary and the things to do.',
  example: VOICE_MEMO_EXAMPLE,
  steps: [
    {
      title: 'Upload the memo',
      body: 'On iPhone, save the memo to Files and choose it here. Android recordings work the same way.',
    },
    LANGUAGE_STEP,
    {
      title: 'Get text and to-dos',
      body: 'Read the transcript, copy it, or generate a summary and a list of action items from it.',
    },
  ],
  details: {
    title: 'Think out loud.',
    tail: 'Get it in writing.',
    items: [
      {
        title: 'Ideas you won’t lose',
        body: 'Every memo becomes searchable text, so the idea you had on a walk is still there next month.',
      },
      {
        title: 'To-do lists from rambling',
        body: 'Get the tasks out of a memo as a checklist, even when you jumped between topics.',
      },
      {
        title: 'Drafts from your voice',
        body: 'Talk through an email, a post or a plan, then turn the memo into a clean first draft.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I transcribe a voice memo from my iPhone?',
      answer:
        'In Voice Memos, tap the recording, then the share button and Save to Files. Open this page, choose the file and the transcript is ready in a minute or two.',
    },
    {
      question: 'Can I transcribe Android voice recordings?',
      answer:
        'Yes. Recordings from Android recorder apps, usually M4A, MP3 or AMR files, can be uploaded the same way.',
    },
    {
      question: 'Can I record a voice note here instead?',
      answer:
        'Yes. The AI Note Taker records in your browser and transcribes the note when you stop.',
    },
  ],
};

export const NOTE_TAKER_PAGE: LandingCopy = {
  id: 'ai-note-taker',
  path: '/ai-note-taker',
  cta: 'record',
  seoTitle: 'Free AI Note Taker for Meetings & Lectures',
  seoDescription:
    'A free AI note taker that records meetings, calls and lectures in your browser, then writes the transcript, a summary and action items when you stop.',
  ogTitle: 'Free AI note taker',
  eyebrow: 'AI note taker',
  title: 'Free AI note taker',
  titleTail: 'for meetings, calls and lectures.',
  lead: 'Press record on your laptop or phone. When you stop, you get the transcript with every speaker, a summary and the action items.',
  example: NOTE_TAKER_EXAMPLE,
  steps: [
    {
      title: 'Press record',
      body: 'Open the AI Note Taker in your browser and press record. It works on laptops and phones, with nothing to install.',
    },
    {
      title: 'Talk as usual',
      body: 'Record for up to 2 hours. The recording uploads in small parts while you talk, so it’s ready soon after you stop.',
    },
    {
      title: 'Get your notes',
      body: 'When you stop, the recording is transcribed and you can generate a summary, action items, minutes and more.',
    },
  ],
  details: {
    title: 'Be in the conversation.',
    tail: 'Not in your notes.',
    items: [
      {
        title: 'Meetings, calls and classes',
        body: 'Team meetings, client calls, interviews, coaching sessions and lectures. Choose the kind of recording and get notes that fit it.',
      },
      {
        title: 'Notes the moment you stop',
        body: 'A summary, action items and the questions raised, written from what was actually said.',
      },
      {
        title: 'No bot in your meetings',
        body: 'Nothing joins your calls or asks to be let in. You decide when to record.',
      },
    ],
  },
  faqs: [
    {
      question: 'Is the AI note taker free?',
      answer:
        'Yes. Every account gets {{free}} free transcriptions a month, and each recording counts as one. Paid plans add more.',
    },
    {
      question: 'Does it join my Zoom, Meet or Teams calls?',
      answer:
        'No bot joins your calls. The note taker records through your microphone, so it works best for meetings in the room. For online calls, upload the call’s recording instead.',
    },
    {
      question: 'How long can I record?',
      answer:
        'Up to 2 hours per recording. Longer events can be recorded in parts.',
    },
    {
      question: 'Can I use it on my phone?',
      answer:
        'Yes. Open the note taker in your phone’s browser and press record. Keep the page open while you record.',
    },
  ],
};

export const LANDING_PAGES: LandingCopy[] = [
  HUB_PAGE,
  AUDIO_TO_TEXT_PAGE,
  VIDEO_TO_TEXT_PAGE,
  MP3_TO_TEXT_PAGE,
  MP4_TO_TEXT_PAGE,
  M4A_TO_TEXT_PAGE,
  WAV_TO_TEXT_PAGE,
  MEETING_PAGE,
  ZOOM_PAGE,
  INTERVIEW_PAGE,
  PODCAST_PAGE,
  LECTURE_PAGE,
  YOUTUBE_PAGE,
  YOUTUBE_SUMMARIZER_PAGE,
  TIKTOK_PAGE,
  INSTAGRAM_PAGE,
  VOICE_MEMO_PAGE,
  NOTE_TAKER_PAGE,
];

/**
 * A language page. {{language}} is the language's name, translated on
 * render; the example is a meeting spoken in that language.
 */
export const languagePage = (
  language: TranscriptionLanguagePage,
  example: TranscriptExample,
): LandingCopy => ({
  id: `language-${language.slug}`,
  path: `/ai-transcription/${language.slug}`,
  seoTitle: '{{language}} Transcription: {{language}} Audio to Text',
  seoDescription:
    'Transcribe {{language}} audio and video to text online. Accurate {{language}} speech to text with speaker labels, timestamps, summaries and subtitles. Free to try.',
  ogTitle: '{{language}} transcription',
  eyebrow: '{{language}} speech to text',
  title: '{{language}} transcription',
  titleTail: 'for audio and video.',
  lead: 'Upload a recording in {{language}} or paste a link and get an accurate transcript with speaker names and timestamps, plus a summary and subtitles.',
  example,
  steps: [
    {
      title: 'Add your recording',
      body: 'Upload an audio or video file, record in your browser, or paste a YouTube, Instagram, TikTok or Google Drive link.',
    },
    {
      title: 'Choose {{language}}',
      body: 'Pick {{language}} as the spoken language for the best accuracy. Turn on speaker detection when more than one person talks.',
    },
    RESULT_STEP,
  ],
  details: {
    title: '{{language}} speech,',
    tail: 'written down accurately.',
    items: [
      {
        title: 'Set to {{language}} from the start',
        body: 'Choosing {{language}} as the spoken language gives the most accurate results with accents, names and fast speech.',
      },
      {
        title: 'Notes from the same recording',
        body: 'Generate a summary, minutes or an article from the {{language}} transcript, or ask the chat to translate it.',
      },
      {
        title: 'Subtitles for your videos',
        body: 'Download {{language}} subtitles as SRT or VTT files for YouTube or your video editor.',
      },
    ],
  },
  faqs: [
    {
      question: 'How do I transcribe {{language}} audio to text?',
      answer:
        'Upload your recording or paste a link on this page, choose {{language}} as the spoken language and start the transcription. The text is ready in a few minutes.',
    },
    {
      question: 'How accurate is {{language}} transcription?',
      answer:
        'Clear recordings come out close to word for word. Choosing {{language}} as the spoken language, rather than automatic detection, gives the best results with accents and names.',
    },
    {
      question: 'Can I translate a {{language}} transcript?',
      answer:
        'Yes. Ask the transcript chat to translate it, or any part of it, into the language you need.',
    },
  ],
  vars: { language: language.name },
});
